import { defineStore } from 'pinia'

import axios from '@axios'
import { reportError } from '@/plugins/misc'
import localforage from 'localforage'

const baseUrl = `/instantBookingSlot/`

export const useInstantBookingSlotsStore = defineStore({
  id: 'instantBookingSlots',
  state: () => ({
    slots: [],
    adminSlots: [],
    currentSlot: null,
    filters: {
      date: null,
      city: null,
      experienceName: null,
      restaurantId: null,
      status: null,
    },
    loading: false,
    analytics: null,
    isInitialized: false,
  }),

  actions: {
    async loadInitialData() {
      if (this.isInitialized) {
        return
      }

      const slots = await localforage.getItem('instantBookingSlots')
      if (slots) {
        this.slots = JSON.parse(slots)
      }

      this.isInitialized = true
    },

    /**
     * Fetch available slots with optional filters (member view)
     */
    async fetchSlots(filters = {}) {
      this.loading = true
      try {
        const params = {}
        if (filters.date) params.date = filters.date
        if (filters.city) params.city = filters.city
        if (filters.experienceName) params.experienceName = filters.experienceName
        if (filters.restaurantId) params.restaurantId = filters.restaurantId

        const res = await axios.get(baseUrl, { params })
        this.slots = res.data.slots
        localforage.setItem('instantBookingSlots', JSON.stringify(this.slots))
      } catch (error) {
        console.error('Error fetching slots:', error)
        reportError('instantBookingSlots.store.js:fetchSlots', error.message, null, null, null, error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch slot by ID
     */
    async fetchSlotById(slotId) {
      this.loading = true
      try {
        const res = await axios.get(`${baseUrl}${slotId}`)
        this.currentSlot = res.data.slot
        
        return res.data.slot
      } catch (error) {
        console.error('Error fetching slot:', error)
        reportError('instantBookingSlots.store.js:fetchSlotById', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch slots for a specific restaurant
     */
    async fetchRestaurantSlots(restaurantId, status = 'available') {
      this.loading = true
      try {
        const res = await axios.get(`${baseUrl}restaurant/${restaurantId}`, {
          params: { status },
        })
        this.slots = res.data.slots
        
        return res.data.slots
      } catch (error) {
        console.error('Error fetching restaurant slots:', error)
        reportError('instantBookingSlots.store.js:fetchRestaurantSlots', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch slots by experience name
     */
    async fetchExperienceSlots(experienceName, city = null) {
      this.loading = true
      try {
        const params = {}
        if (city) params.city = city

        const res = await axios.get(`${baseUrl}experience/${experienceName}`, { params })
        this.slots = res.data.slots
        
        return {
          slots: res.data.slots,
          experienceMetadata: res.data.experienceMetadata,
        }
      } catch (error) {
        console.error('Error fetching experience slots:', error)
        reportError('instantBookingSlots.store.js:fetchExperienceSlots', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Book a slot (member action)
     */
    async bookSlot(slotId, paymentMethodId, specialRequests = null) {
      this.loading = true
      try {
        const res = await axios.post(`${baseUrl}${slotId}/book`, {
          paymentMethodId,
          specialRequests,
        })
        
        return res.data
      } catch (error) {
        console.error('Error booking slot:', error)
        reportError('instantBookingSlots.store.js:bookSlot', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Cancel a booking
     */
    async cancelBooking(slotId, reservationId) {
      this.loading = true
      try {
        const res = await axios.post(`${baseUrl}${slotId}/cancel`, {
          reservationId,
        })
        
        return res.data
      } catch (error) {
        console.error('Error canceling booking:', error)
        reportError('instantBookingSlots.store.js:cancelBooking', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Request transfer for non-refundable booking
     */
    async requestTransfer(slotId) {
      this.loading = true
      try {
        const res = await axios.post(`${baseUrl}${slotId}/request-transfer`)
        
        return res.data
      } catch (error) {
        console.error('Error requesting transfer:', error)
        reportError('instantBookingSlots.store.js:requestTransfer', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    // ============================================================================
    // ADMIN ACTIONS
    // ============================================================================

    /**
     * Create new instant booking slots (batch creation)
     * Admin only
     */
    async createSlots(slotsData) {
      this.loading = true
      try {
        const res = await axios.post(baseUrl, slotsData)

        // Refresh admin slots after creation
        if (slotsData.restaurantId) {
          await this.fetchAdminRestaurantSlots(slotsData.restaurantId)
        }

        return res.data
      } catch (error) {
        console.error('Error creating slots:', error)
        reportError('instantBookingSlots.store.js:createSlots', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Update existing slot
     * Admin only
     */
    async updateSlot(slotId, updates) {
      this.loading = true
      try {
        const res = await axios.put(`${baseUrl}${slotId}`, updates)

        // Update local admin state
        const index = this.adminSlots.findIndex(s => s._id === slotId)
        if (index !== -1) {
          this.adminSlots[index] = res.data.slot
        }

        return res.data.slot
      } catch (error) {
        console.error('Error updating slot:', error)
        reportError('instantBookingSlots.store.js:updateSlot', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Delete slot
     * Admin only
     */
    async deleteSlot(slotId) {
      this.loading = true
      try {
        await axios.delete(`${baseUrl}${slotId}`)

        // Remove from local admin state
        this.adminSlots = this.adminSlots.filter(s => s._id !== slotId)

        return true
      } catch (error) {
        console.error('Error deleting slot:', error)
        reportError('instantBookingSlots.store.js:deleteSlot', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch all slots across all restaurants (admin view)
     * Admin or restaurant admin only
     * @param {Object} filters - Filter options
     * @param {string} filters.status - Filter by slot status
     * @param {string} filters.date - Filter by date
     * @param {string} filters.experienceName - Filter by experience name
     * @param {string[]} filters.restaurantIds - Filter by restaurant IDs (for restaurant admins)
     */
    async fetchAdminAllSlots(filters = {}) {
      this.loading = true
      try {
        const params = {}
        if (filters.status) params.status = filters.status
        if (filters.date) params.date = filters.date
        if (filters.experienceName) params.experienceName = filters.experienceName
        if (filters.restaurantIds && filters.restaurantIds.length > 0) {
          params.restaurantIds = filters.restaurantIds.join(',')
        }

        const res = await axios.get(`${baseUrl}admin/all`, { params })
        this.adminSlots = res.data.slots

        return res.data.slots
      } catch (error) {
        console.error('Error fetching admin all slots:', error)
        reportError('instantBookingSlots.store.js:fetchAdminAllSlots', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch all slots for a restaurant (admin view)
     * Admin only
     */
    async fetchAdminRestaurantSlots(restaurantId, filters = {}) {
      this.loading = true
      try {
        const params = {}
        if (filters.status) params.status = filters.status
        if (filters.date) params.date = filters.date
        if (filters.experienceName) params.experienceName = filters.experienceName

        const res = await axios.get(`${baseUrl}admin/restaurant/${restaurantId}`, { params })
        this.adminSlots = res.data.slots

        return res.data.slots
      } catch (error) {
        console.error('Error fetching admin restaurant slots:', error)
        reportError('instantBookingSlots.store.js:fetchAdminRestaurantSlots', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch booking analytics
     * Admin only
     */
    async fetchAnalytics(experienceName = null) {
      this.loading = true
      try {
        const params = {}
        if (experienceName) params.experienceName = experienceName

        const res = await axios.get(`${baseUrl}admin/analytics`, { params })
        this.analytics = res.data
        
        return res.data
      } catch (error) {
        console.error('Error fetching analytics:', error)
        reportError('instantBookingSlots.store.js:fetchAnalytics', error.message, null, null, null, error)
        throw error
      } finally {
        this.loading = false
      }
    },
  },

  getters: {

    /**
     * Get slots grouped by date
     */
    slotsByDate: state => {
      const grouped = {}

      state.slots.forEach(slot => {
        const dateKey = slot.date.split('T')[0]
        if (!grouped[dateKey]) {
          grouped[dateKey] = {
            date: dateKey,
            slots: [],
          }
        }
        grouped[dateKey].slots.push(slot)
      })

      return Object.values(grouped).sort((a, b) =>
        new Date(a.date) - new Date(b.date)
      )
    },

    /**
     * Get slots grouped by restaurant
     */
    slotsByRestaurant: state => {
      const grouped = {}

      state.slots.forEach(slot => {
        const key = slot.restaurantId
        if (!grouped[key]) {
          grouped[key] = {
            restaurantId: slot.restaurantId,
            restaurantName: slot.restaurantName,
            restaurantMarket: slot.restaurantMarket,
            slots: [],
          }
        }
        grouped[key].slots.push(slot)
      })

      return Object.values(grouped)
    },

    /**
     * Get unique experience names
     */
    experienceNames: state => {
      const names = new Set()
      state.slots.forEach(slot => {
        if (slot.experienceName) names.add(slot.experienceName)
      })
      
      return Array.from(names).sort()
    },

    /**
     * Get unique cities/markets
     */
    markets: state => {
      const markets = new Set()
      state.slots.forEach(slot => {
        if (slot.restaurantMarket) markets.add(slot.restaurantMarket)
      })
      
      return Array.from(markets).sort()
    },
  },
})
